import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import {
  CONSULAR_DOC_LANDINGS,
  getConsularDocLanding,
  type ConsularDocLanding,
} from "@/content/consular-corpus/documents";

function faqsEn(d: ConsularDocLanding) {
  return [
    { q: `What are the steps for ${d.titleEn.toLowerCase()}?`, a: d.chainEn.join(" → ") },
    { q: "Which authorities accept this document?", a: d.useCaseEn },
    { q: "What do I need to prepare?", a: d.prepareEn.join(" · ") },
    { q: "Why do these documents get rejected?", a: d.pitfallsEn.join(" · ") },
    { q: "How long does it take and what does it cost?", a: d.turnaroundEn },
    { q: "Can I use an Apostille from Thailand yet?", a: d.apostilleNoteEn },
    {
      q: "Can an agent file the legalisation on my behalf?",
      a: "Yes. The agent needs a power of attorney with a THB 10 duty stamp plus certified ID copies of both the principal and the agent. If the principal is abroad, a Thai embassy or consulate should verify the signature first.",
    },
    {
      q: "Where is legalisation filed, and is an appointment required?",
      a: "Filings are made at the Legalization Division, Department of Consular Affairs on Chaeng Watthana Road and at its announced regional offices. Walk-in and advance appointments are both accepted; 08:00–11:30 is the busiest window. Our team files door-to-door from Phuket and nationwide.",
    },
    {
      q: "How is certified translation different from legalisation?",
      a: "A certified translation attests that the translation matches the original. Legalisation is the Department of Consular Affairs verifying the signature and seal of the issuing or certifying official so the document has effect abroad. They are separate steps and usually performed in sequence.",
    },
  ];
}

export const Route = createFileRoute("/en/consular-legalization/document/$doc")({
  loader: ({ params }: { params: { doc: string } }) => {
    const d = getConsularDocLanding(params.doc);
    if (!d) throw notFound();
    return { d };
  },
  component: ConsularDocPageEN,
  head: ({ params, loaderData }: { params: { doc: string }; loaderData?: { d: ConsularDocLanding } }) => {
    if (!loaderData) {
      return { meta: [{ title: "Document type not found" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { d } = loaderData;
    return {
      ...pageHeadForProvince({
        title: `${d.titleEn} 2026 — Steps, Fees & Requirements`,
        description: `${d.titleEn}: the certification chain, required documents, THB 200/400 government fees, common rejection causes and Thailand's 2027 Apostille timeline.`,
        path: `/en/consular-legalization/document/${params.doc}`,
        type: "article",
        alternates: [{ lang: "th", path: `/consular-legalization/document/${params.doc}` }],
        keywords: [d.titleEn, "MFA legalization Thailand", "consular legalisation", "Thailand Apostille 2027"],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Consular Legalisation", url: `${site}/en/consular-legalization` },
              { name: d.titleEn, url: `${site}/en/consular-legalization/document/${params.doc}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqsEn(d))) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: d.titleEn,
            step: d.chainEn.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s })),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/en/consular-legalization/document/${params.doc}`)) },
      ],
    };
  },
});

function ConsularDocPageEN() {
  const { d } = Route.useLoaderData();
  const faqs = faqsEn(d);
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <a href="/en/consular-legalization/faq" className="hover:underline">Consular Legalization</a> <span>/</span>{" "}
          <span>{d.titleEn}</span>
        </nav>

        <h1 className="mb-3 text-3xl font-bold md:text-4xl">{d.titleEn} (2026)</h1>
        <p className="mb-6 text-lg text-muted-foreground" data-speakable="true">
          Source document: <strong>{d.issuerEn}</strong>
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-sm">
          <a href="/contact" className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground">Get a free quote →</a>
          <a href="/en/consular-legalization/faq" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">Legalization FAQ</a>
          <a href={`/consular-legalization/document/${d.slug}`} className="rounded-md border px-4 py-2 text-muted-foreground hover:bg-muted">ภาษาไทย →</a>
        </div>

        <section className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="mb-3 text-xl font-bold">Certification chain</h2>
          <ol className="list-decimal space-y-1 pl-6 text-sm text-muted-foreground">
            {d.chainEn.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ol>
          <p className="mt-4 text-sm text-muted-foreground"><strong>Timing and fees:</strong> {d.turnaroundEn}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">What it is used for</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{d.useCaseEn}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">What to prepare</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {d.prepareEn.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Common rejection causes</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {d.pitfallsEn.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
        </section>

        <section className="mb-10 rounded-lg border bg-muted/20 p-6">
          <h2 className="mb-3 text-xl font-bold">Apostille timeline for this document</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{d.apostilleNoteEn}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Frequently asked questions</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="rounded-lg border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                <summary className="cursor-pointer text-sm font-semibold">{f.q}</summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Legalise other document types</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {CONSULAR_DOC_LANDINGS.filter((x) => x.slug !== d.slug).map((x) => (
              <a key={x.slug} href={`/en/consular-legalization/document/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.titleEn}
              </a>
            ))}
          </div>
        </section>

        <p className="mb-6 text-xs text-muted-foreground">
          Information current as of August 2026. Procedures and fees follow the Legalization Division, Department of
          Consular Affairs (consular.mfa.go.th); Apostille status follows hcch.net (in force for Thailand on 28 February
          2027). Always confirm requirements with the receiving authority before filing.
        </p>
      </main>
      <SiteFooter />
    </>
  );
}
